import { createContext, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from './useLocalStorage';
import { NavigationRoutes } from '../../../application/router/routes.ts';
import { IdpToken } from '../../../application/types/IdpToken.ts';
import { ProfileService } from '../../profile/Services/ProfileService.ts';
import { useGlobalStore } from '../../../application/store/store.tsx';
import AuthService from '../services/authService.ts';
import { FeatureConfigService } from '../../../common/services/featureConfigService.ts';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [tokens, setTokens] = useSessionStorage('tokens', null);
    const navigate = useNavigate();
    const { setRegistrationMethod } = useGlobalStore();

    const login = async (username: string, password: string) => {
        const orgId = JSON.parse(localStorage.getItem('orgId'));
        const res = await AuthService.login(username, password, orgId);

        if (res.status == 200) {
            setTokens(res.data);
            const profileService = new ProfileService();
            const profile = await profileService.getProfile();
            if (profile.requirePasswordChange) {
                navigate(
                    NavigationRoutes.profile +
                        '/' +
                        NavigationRoutes.updatePassword
                );
            } else {
                navigate(NavigationRoutes.transaction, { replace: true });
            }

            setRegistrationMethod(profile.tenantConfig.formRegistrationMethod);
        }
    };

    const loginIdp = async (idpToken: IdpToken) => {
        const orgId = JSON.parse(localStorage.getItem('orgId'));
        const res = await AuthService.loginIdp(idpToken, orgId);

        if (res.status === 200) {
            setTokens(res.data);
            const profileService = new ProfileService();
            const profile = await profileService.getProfile();
            if (profile.requirePasswordChange) {
                navigate(
                    NavigationRoutes.profile +
                        '/' +
                        NavigationRoutes.updatePassword
                );
            } else {
                navigate(NavigationRoutes.transaction, { replace: true });
            }
        }
    };

    const logout = async () => {
        AuthService.logout().then(() => {
            useGlobalStore.getState().setProfile(null);
            setTokens(null);
            sessionStorage.clear();

            // Clear features when logging out
            const featureService = FeatureConfigService.getInstance();
            featureService.clearFeatures();

            navigate(NavigationRoutes.login, { replace: true });
        });
    };

    const value = useMemo(
        () => ({
            tokens,
            loginIdp,
            login,
            logout,
        }),
        [tokens]
    );
    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
